// Purchase order detail — /procurement/po/:id. ERP expansion chunk 1, phase 6.
//
// Header card (vendor/dates/terms/ship-to/notes), lines table, progress,
// timeline, and the lifecycle actions for whatever status the PO is in. All
// API-shape assumptions live in poApi.ts.
//
// Approve/Deny render for EVERYONE on a pending_approval PO (not just the
// configured approvers) — the backend enforces approver identity and 403s
// non-approvers; this page just surfaces whatever message comes back. That is
// the approval-flow phase's design, not a shortcut taken here.
//
// EDIT MODE. Phase 6 shipped the lifecycle actions and left the mutation
// surface for later, so the only editable thing on this page was the note —
// which meant Amend handed you an -R1 draft you could not adjust, the one job
// amending exists for. Edit now covers the header and every line, gated on the
// same rule PurchaseOrdersService.update enforces: a draft, or anything whose
// origin is not 'app' (imported history stays correctable at any status).
//   * All drafts live in poEditDraft.ts, which also builds the PATCH. One save,
//     one request, carrying ONLY what changed.
//   * The lifecycle buttons are HIDDEN while editing. Submit mid-edit would
//     discard unsaved work silently, and there is no wording that makes that
//     acceptable.
//   * Adding a line and swapping the vendor are deliberately not here — see the
//     header comment in poEditDraft.ts for why.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import type { ColumnDef, HeaderContext, Table } from '@tanstack/react-table';
import { useToast } from '../components/Toast/ToastContext';
import { useMatchBoardApi } from './matchBoardApi';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import { formatEventDate, formatTimestamp } from '../utils/date-utils';
import HelpDrawer from '../components/HelpDrawer';
import { DataTable, type GroupByOpts } from '../components/table/DataTable';
import MessageThread from '../components/MessageThread/MessageThread';
// Name-first, with ONE deliberate exception kept: a purchase order line is a
// document the VENDOR reads too, so the SKU stays as a muted second line here.
// It is not kept on the internal worklists (match board, returns, reorder).
import ItemName, { itemLabel } from '../components/ItemName';
import { openAuthedPdf } from '../Receiving/openAuthedPdf';
import VendorPreviewModal from './VendorPreviewModal';
import PaymentClockFields from './PaymentClockFields';
import { describeClock, estimateReason, waitingLabel } from './paymentClock';
import { useApi } from '../apiHelpers';
import ReturnItemsDialog from './ReturnItemsDialog';
import {
  usePoApi,
  formatCents,
  pdfUrl,
  PO_STATUS_LABELS,
  PO_LINE_STATUS_LABELS,
  PO_LINE_TYPE_LABELS,
  type PoDetail,
  type PoLine,
  type PoStatus,
} from './poApi';
import {
  buildPatch,
  draftLineTotalCents,
  describeDiscount,
  discountOf,
  draftDiscountCents,
  draftSubtotalCents,
  draftTotalCents,
  headerDraftFrom,
  isChargeLine,
  isNewLineId,
  isRecordedReduction,
  lineDraftFrom,
  lineDraftsFrom,
  lineLabel,
  lineOrderChanged,
  linePositions,
  newLineId,
  patchReasksApproval,
  pinnedLineIds,
  stagedCopyOf,
  stagedFromEntry,
  validateDrafts,
  type HeaderDraft,
  type LineDraft,
  type LineDraftMap,
} from './poEditDraft';
// Moving a line: the grip cell and the drag context, shared with the create form.
import { CARRIED_ROW, LineGrip, LineOrder, insertPinned, movePinned } from './LineOrder';
// The create form's entry row, shared: Add line in edit mode is the same
// item/qty/cost row as New purchase order, so the two never drift apart.
import PoLineEntry, { type PoLineEntryHandle, type StagedDiscount, type StagedLine } from './PoLineEntry';
import useUserStore from '../stores/useUserStore';
import PoBillingPanel from './PoBillingPanel';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Appear } from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { Checkbox } from '../components/ui/Checkbox';
import Dialog from '../components/ui/Dialog';
import { EmptyState } from '../components/ui/EmptyState';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { Panel } from '../components/ui/Panel';
import { Progress } from '../components/ui/Progress';
import { Field as TextField, TextArea } from '../components/ui/Field';
import { FilePick } from '../components/ui/FilePick';
import { Tag } from '../components/ui/Tag';
import { Tooltip } from '../components/ui/Tooltip';
import { PO_LINE_STATUS_TAG, PO_LINE_TYPE_TAG, PO_STATUS_TAG } from './poTones';
import {
  useApInvoicesApi,
  type OrderBilling,
  type OrderLineBilling,
  type VendorInvoice,
} from '../AccountsPayable/apInvoicesApi';

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

type ReasonKind = 'cancel' | 'close' | 'deny' | 'reopen';

const REASON_COPY: Record<
  ReasonKind,
  { title: string; fieldLabel: string; required: boolean; confirmLabel: string; danger: boolean }
> = {
  cancel: {
    title: 'Cancel this purchase order',
    fieldLabel: 'Reason (required)',
    required: true,
    confirmLabel: 'Cancel order',
    danger: true,
  },
  close: {
    title: 'Stop expecting the rest of this order',
    // Required now. Closing is the moment a shortfall becomes permanent, and
    // "why did this stop three units short" is the first thing an audit asks.
    fieldLabel: 'Why is it stopping here? (required)',
    required: true,
    confirmLabel: 'Close order',
    danger: false,
  },
  deny: {
    title: 'Deny this purchase order',
    fieldLabel: 'Reason (required)',
    required: true,
    confirmLabel: 'Deny',
    danger: true,
  },
  reopen: {
    title: 'Reopen this purchase order',
    // Required, same standard as close: undoing a permanent decision needs a
    // why on the record.
    fieldLabel: 'Why is it reopening? (required)',
    required: true,
    confirmLabel: 'Reopen order',
    danger: false,
  },
};

/** The Tag springs when `status` changes under it, which is what makes the result of
 * approve/deny/submit/cancel/close/amend land: each replaces the whole PO object. */
const StatusChip: React.FC<{ status: PoStatus }> = ({ status }) => (
  <Tag {...PO_STATUS_TAG[status]}>{PO_STATUS_LABELS[status]}</Tag>
);

/** The edit-mode twin of Field: same slot in the same grid, so entering edit
 * mode does not reflow the header card. */
const EditField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date';
  placeholder?: string;
  /** Hard field on a document past pending_approval. Disabled with the reason
   * shown, rather than hidden: a field that vanishes reads as a bug, and the
   * value still matters even when it cannot be changed. */
  locked?: boolean;
  lockedHint?: string;
}> = ({ label, value, onChange, type = 'text', placeholder, locked, lockedHint }) => (
  <label className="block">
    <span className={labelClass}>{label}</span>
    <TextField
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={locked}
      onChange={(e) => onChange(e.target.value)}
    />
    {locked && lockedHint && (
      <span className="block text-[11px] text-on-surface-variant mt-0.5">{lockedHint}</span>
    )}
  </label>
);

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-on-surface-variant">{label}</div>
    <div className="text-on-surface text-sm">{value}</div>
  </div>
);

const OriginNote: React.FC<{ origin: PoDetail['origin'] }> = ({ origin }) => {
  if (origin === 'app') return null;
  return (
    <Tooltip label="History brought in from the old spreadsheets, still editable.">
      <Tag size="sm">{origin === 'backfill' ? 'Imported' : 'Imported (PDF)'}</Tag>
    </Tooltip>
  );
};

/** Table-cell input. type="text" even for the numeric fields on purpose:
 * index.css has a global `input[type="number"] { padding: 0; padding-top: 10px;
 * margin: auto; border: 1px solid black }` that would blow these cells apart
 * (see .plans/neutralize-global-input-css.md). inputMode still gets the right
 * mobile keypad, and text inputs have no spinner to scroll a quantity by
 * accident. */
// ONE line table, both modes. These three cells are pure presentation over a
// row, so they are held here and the column defs below consume them from both
// the read-only and the edit branch.
const typePill = (line: PoLine) => (
  <Tag size="sm" {...PO_LINE_TYPE_TAG[line.line_type]}>{PO_LINE_TYPE_LABELS[line.line_type]}</Tag>
);

const statusPill = (line: PoLine) => (
  <Tag {...PO_LINE_STATUS_TAG[line.status]}>{PO_LINE_STATUS_LABELS[line.status]}</Tag>
);

const skuContent = (line: PoLine) =>
  line.line_type !== 'stock' ? (
    <span className="text-on-surface-disabled">—</span>
  ) : !line.part_catalog_id ? (
    <span className="text-on-surface-variant italic font-sans">no SKU yet</span>
  ) : line.sku ? (
    <ItemName size="sm" showSku item={line} />
  ) : (
    // A cataloged line whose SKU did not come back. parts_catalog.sku is NOT
    // NULL, so this is defense rather than a case anyone will see.
    <span className="text-primary font-mono">{`#${line.part_catalog_id}`}</span>
  );

/** The catalog's description, but only when it says something the item's NAME
 * does not. A generated name is built FROM that description, so showing both
 * puts the same words in two columns of the same row. */
const extraDescription = (line: PoLine): string | null => {
  const d = line.part_description;
  if (!d) return null;
  return d.trim() === itemLabel(line).trim() ? null : d;
};

// ---------------------------------------------------------------------------
// Edit mode's volatile half, carried by the table's `meta`.
//
// The columns below are module scope and never change identity, which is the
// DataTable's first contract — a fresh columns array hands flexRender a new
// component type, React remounts the cell, and the caret leaves the input
// mid-word. Drafts change on every keystroke, so they travel by `meta`
// instead: tanstack replaces its whole options object during render, so a cell
// reads the CURRENT drafts on the same pass the page handed them over, while
// keeping its own DOM node. See web/docs/features/shared-table.md.
// ---------------------------------------------------------------------------

/** What the page puts in `meta` while a PO with ANY editable line key is being
 * edited. Absent means read-only rows, which is both "not editing" and "editing
 * a soft-only document" (its lines are the document's substance and stay
 * fixed). `editable` is the server's answer to which keys are open: a cell
 * asks it and renders its read branch for a key that is not, so an issued
 * order's row shows a quantity input beside a cost that is plain text. */
interface PoLineEditMeta {
  drafts: LineDraftMap;
  removedIds: string[];
  editable: PoDetail['editable'];
  /** Each surviving line's number as the save would write it. Present only
   * once a line has moved; until then the stored number is the truth. */
  positions?: Record<string, number>;
  onChangeLine: (lineId: string, patch: Partial<LineDraft>) => void;
  onToggleRemove: (lineId: string) => void;
  onDuplicate: (line: PoLine) => void;
  onStep: (lineId: string, delta: -1 | 1) => void;
}

/** Everything edit mode needs for one row. Absent = read-only row, which is
 * every row on a PO nobody may edit. */
interface LineEditHandle {
  draft: LineDraft;
  removed: boolean;
  /** A staged copy with no database row behind it. Changes what Remove MEANS on
   * this row: discarding an unsaved copy, not marking a stored line for
   * deletion. */
  isNew: boolean;
  /** May this line key be written on this document right now? */
  canEdit: (field: string) => boolean;
  /** The draft quantity is below the stored one on a document that records
   * reductions, so the row owes a reason. */
  reduced: boolean;
  /** The number this row will carry after the save, once any line has moved. */
  position?: number;
  onChange: (patch: Partial<LineDraft>) => void;
  onToggleRemove: () => void;
  onDuplicate: () => void;
  onStep: (delta: -1 | 1) => void;
}

/** The table's whole meta: edit mode's volatile half, plus the billing read
 * for the two financial-only columns. Either half may be absent — a buyer's
 * read-only table carries neither, edit mode carries only `edit`, a financial
 * read view carries only `billing`. */
interface PoTableMeta {
  edit?: PoLineEditMeta;
  billing?: Map<string, OrderLineBilling>;
}

/** One row's handle, off the table's current meta. NEVER memoize a cell that
 * calls this: tanstack memoizes the cell's context object, so the render that
 * picks up a new draft is the parent's — a React.memo'd cell skips it and
 * paints a stale draft (the documented stale-meta gotcha). */
const editHandleFor = (table: Table<PoLine>, line: PoLine): LineEditHandle | undefined => {
  const m = (table.options.meta as PoTableMeta | undefined)?.edit;
  const draft = m?.drafts[line.id];
  if (!m || !draft) return undefined;
  return {
    draft,
    removed: m.removedIds.includes(line.id),
    isNew: isNewLineId(line.id),
    canEdit: (field) => m.editable.line_fields.includes(field),
    reduced: isRecordedReduction(line, draft, m.editable),
    position: m.positions?.[line.id],
    onChange: (patch) => m.onChangeLine(line.id, patch),
    onToggleRemove: () => m.onToggleRemove(line.id),
    onDuplicate: () => m.onDuplicate(line),
    onStep: (delta) => m.onStep(line.id, delta),
  };
};

/** The four presentation-only cells need the strike-through and nothing else,
 * so they skip building a handle. Same membership question, asked cheaper. */
const isRemoved = (table: Table<PoLine>, line: PoLine): boolean => {
  const m = (table.options.meta as PoTableMeta | undefined)?.edit;
  return !!m && !!m.drafts[line.id] && m.removedIds.includes(line.id);
};

/** One line's billing read, for the two invoice columns. Absent while the
 * fetch is out, for buyers, and for a staged copy (no row, no billing). */
const billingFor = (table: Table<PoLine>, line: PoLine): OrderLineBilling | undefined =>
  (table.options.meta as PoTableMeta | undefined)?.billing?.get(line.id);

/** Removal strikes through the CELL, not the row: the row's red wash arrives by
 * rowProps, and the Remove column stays out of this so its Keep button reads as
 * a live control on an otherwise struck-out row. */
const struck = (removed: boolean, content: React.ReactNode): React.ReactNode =>
  removed ? <span className="opacity-40 line-through">{content}</span> : content;

/** The quantity the arithmetic should measure against: the DRAFT while a row is
 * being edited, so the over badge and Remaining say what the save would mean.
 * ONE guarded value feeds both, because the quantity field is free text (see
 * the cell inputs) — a half-typed quantity is NaN, and an unguarded subtraction
 * renders "+NaN over". A row marked for removal falls back to the stored figure:
 * its numbers are struck through, not restated. */
const effectiveQtyFor = (line: PoLine, edit?: LineEditHandle): number => {
  const draftQty = edit ? Number(edit.draft.quantity_expected) : NaN;
  return edit && !edit.removed && Number.isFinite(draftQty) ? draftQty : line.quantity_expected;
};

/** What this line has NET, which is the only figure that answers "is it done".
 * `quantity_received` is gross and stays gross: it is the delivery record. The
 * server derives `quantity_returned` off the non-cancelled purchase returns and
 * builds the line's status on this same difference, so the Remaining column and
 * the over badge read it too rather than the raw arrival count. */
const netReceivedFor = (line: PoLine): number =>
  line.quantity_received - (line.quantity_returned || 0);

// ---------------------------------------------------------------------------
// The lines table: the shared DataTable's columns, serving BOTH modes.
//
// Module scope, per the DataTable contract — the arrays never change identity
// and no cell closes over page state. A cell's read-only branch is pure over
// its row; its edit branch reads the drafts out of `meta` (above), which is the
// only way one stable column can hold an input the user is typing in.
//
// No `sortable` and no accessorFn anywhere: a purchase order's lines are read in
// DOCUMENT order (line_number, the order the server sends), and a sortable
// line 3-1-2 is a different document. Edit mode leans on that: the Remove
// buttons are in document order because nothing reorders the rows.
// ---------------------------------------------------------------------------

/** The lines the arithmetic counts. Services and charges have no units, and a
 * cancelled line is no longer owed, which is exactly how the header's
 * percent_received is computed. One predicate, so the totals band cannot
 * appear over a set of lines it would then decline to count. */
const isCountedStock = (line: PoLine) => line.line_type === 'stock' && line.status !== 'cancelled';

/** A columnDef footer gets a HeaderContext, so there is no row in scope: the
 * rows come off the table's own row model. */
const countedStockRows = (ctx: HeaderContext<PoLine, any>): PoLine[] =>
  ctx.table.getRowModel().rows.map((r) => r.original).filter(isCountedStock);

/** The order's arithmetic, done for the reader, one figure per column so each
 * total sits under the column it totals. Keyed by column id and mapped onto the
 * base array below, because contract 1 forbids rebuilding `columns` per render
 * to add or drop a footer. */
const FOOTERS: Record<string, ColumnDef<PoLine, any>['footer']> = {
  // The label. An inner div so its right-alignment and muted tone never fight
  // the td's own class list; it hugs Expected the way the old colSpan cell did.
  description: () => <div className="text-right font-normal text-on-surface-variant">Stock totals</div>,
  expected: (ctx) => countedStockRows(ctx).reduce((n, l) => n + l.quantity_expected, 0),
  // Gross, matching the column: the total under Received is what arrived on
  // this order, and netting returns into it would make the two disagree.
  received: (ctx) => countedStockRows(ctx).reduce((n, l) => n + l.quantity_received, 0),
  remaining: (ctx) => {
    const remaining = countedStockRows(ctx).reduce(
      (n, l) => n + Math.max(l.quantity_expected - netReceivedFor(l), 0),
      0
    );
    return <span className={remaining > 0 ? 'text-on-surface' : 'text-on-surface-disabled'}>{remaining}</span>;
  },
};

const PO_LINE_COLUMNS: ColumnDef<PoLine, any>[] = [
  {
    // The grip. Edit mode only, and only while the shape of the order is open
    // (`columnVisibility` on the page): moving a line renumbers the document,
    // which the vendor's copy then disagrees with, so it follows the same gate
    // as Add line and Remove. A row on its way out has nothing to move.
    id: 'move',
    header: '',
    meta: { align: 'center', cellClass: 'w-8 pr-0' },
    cell: ({ row, table }) => {
      const line = row.original;
      const edit = editHandleFor(table, line);
      if (!edit || edit.removed) return null;
      return <LineGrip id={line.id} label={lineLabel(line)} onStep={edit.onStep} />;
    },
  },
  {
    id: 'number',
    header: '#',
    meta: { numeric: true, cellClass: 'text-on-surface-variant' },
    // A staged copy shows NEW, not a number. The server assigns line_number when
    // it appends, so any number shown here would be this page guessing — and the
    // guess moves every time a removal is staged or taken back. A STORED line
    // shows the number the save will write once any line has moved (the
    // handle's `position`), and its stored number until then.
    cell: ({ row, table }) => {
      const line = row.original;
      if (isNewLineId(line.id)) {
        return (
          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/15 text-primary-ink ring-1 ring-inset ring-primary/30">
            New
          </span>
        );
      }
      const edit = editHandleFor(table, line);
      return struck(!!edit?.removed, edit?.position ?? line.line_number);
    },
  },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row, table }) => struck(isRemoved(table, row.original), typePill(row.original)),
  },
  {
    // The column id stays `sku` — it is what the table's own plumbing keys on.
    // Only what the buyer reads changed.
    id: 'sku',
    header: 'Item',
    cell: ({ row, table }) => struck(isRemoved(table, row.original), skuContent(row.original)),
  },
  {
    id: 'description',
    header: 'Description',
    // Truncation and the tooltip live on a span INSIDE the cell, not on the td:
    // one static column has to serve both modes, and `cellAttrs` takes the row
    // rather than the table, so it cannot see whether this row is being edited.
    // The read-only span truncates and carries the tooltip; the edit span
    // reserves the input its width.
    meta: { cellClass: 'text-on-surface' },
    cell: ({ row, table }) => {
      const line = row.original;
      const edit = editHandleFor(table, line);
      const catalogText = extraDescription(line);
      if (!edit || !edit.canEdit('notes')) {
        return (
          <Tooltip label={line.notes || catalogText || ''} truncated>
            <span className="block max-w-xs truncate">
              {line.notes || catalogText || '—'}
            </span>
          </Tooltip>
        );
      }
      // The strike-through joins this span rather than wrapping it: a block
      // element inside an inline one would drop the min-width.
      return (
        <span className={`block min-w-[14rem]${edit.removed ? ' opacity-40 line-through' : ''}`}>
          <TextField
            size="sm"
            type="text"
            value={edit.draft.notes}
            disabled={edit.removed}
            placeholder={catalogText || 'Description'}
            aria-label={`${lineLabel(line)} description`}
            onChange={(e) => edit.onChange({ notes: e.target.value })}
          />
        </span>
      );
    },
  },
  {
    id: 'expected',
    header: 'Expected',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row, table }) => {
      const line = row.original;
      const edit = editHandleFor(table, line);
      // What the line said before a vendor short-fill lowered it (migration
      // 073). Under the figure, the same shape as "N returned" one cell right:
      // the big number is the order as it stands, this line is why it moved.
      const original = line.quantity_expected_original;
      // Truncated like the Description cell: the reason is buyer free text and
      // an unbounded line would turn a number column into a text column. The
      // full sentence is on the title, and on the quantity_reduced event in
      // Activity, which is the record anyway.
      const reasonText = line.quantity_change_reason ? `: ${line.quantity_change_reason}` : '';
      const originally = original != null && original !== line.quantity_expected ? (
        <Tooltip label={`Originally ${original}${reasonText}`} truncated>
          <span className="block max-w-[10rem] truncate text-[11px] font-normal text-on-surface-variant">
            Originally {original}
            {reasonText}
          </span>
        </Tooltip>
      ) : null;
      if (!edit || !edit.canEdit('quantity_expected')) {
        return (
          <>
            {line.quantity_expected}
            {originally}
          </>
        );
      }
      return struck(
        edit.removed,
        <>
          <TextField
            size="sm"
            type="text"
            inputMode="numeric"
            className="!w-16 text-right"
            value={edit.draft.quantity_expected}
            disabled={edit.removed}
            aria-label={`${lineLabel(line)} expected quantity`}
            onChange={(e) => edit.onChange({ quantity_expected: e.target.value })}
          />
          {/* The reason appears the moment the draft drops below the stored
              figure on an issued order, and nowhere else: the server refuses
              the reduction without it, so the row asks for it where the number
              was typed rather than the save explaining afterwards. */}
          <Reveal show={edit.reduced}>
            <TextField
              size="sm"
              type="text"
              className="mt-1 min-w-[10rem] !max-w-[11rem] text-left"
              value={edit.draft.reason}
              disabled={edit.removed}
              placeholder="Why it came down"
              aria-label={`${lineLabel(line)} reduction reason`}
              onChange={(e) => edit.onChange({ reason: e.target.value })}
            />
          </Reveal>
          {originally}
        </>
      );
    },
  },
  {
    id: 'received',
    header: 'Received',
    meta: { align: 'right', numeric: true },
    cell: ({ row, table }) => {
      const line = row.original;
      const edit = editHandleFor(table, line);
      // Against the DRAFT quantity while this row is being edited, so the badge
      // says what the save would mean; against the stored one otherwise.
      const effectiveQty = effectiveQtyFor(line, edit);
      // The badge is about what the vendor still owes, so it fires on NET: a
      // line that took 12 against 10 and sent 2 back is level, not over.
      const net = netReceivedFor(line);
      const over = net > effectiveQty;
      const returned = line.quantity_returned || 0;
      return struck(
        !!edit?.removed,
        <>
          <span className={over ? 'text-amber-400 font-medium' : 'text-on-surface'}>
            {line.quantity_received}
          </span>
          <Appear show={over} className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30">
            +{net - effectiveQty} over
          </Appear>
          {/* The big number stays what arrived. This second line is why
              Remaining beside it does not agree with the subtraction a reader
              would otherwise do in their head. */}
          <Reveal show={returned > 0}>
            <span className="block text-[11px] text-on-surface-variant">{returned} returned</span>
          </Reveal>
        </>
      );
    },
  },
  {
    id: 'remaining',
    header: 'Remaining',
    meta: { align: 'right', numeric: true },
    // The subtraction the reader was doing in their head. Floored at zero: the
    // over case already has its badge one cell left. Follows the draft while
    // editing, the same way the badge does.
    cell: ({ row, table }) => {
      const line = row.original;
      const edit = editHandleFor(table, line);
      if (line.line_type !== 'stock' || line.status === 'cancelled') {
        return struck(!!edit?.removed, <span className="text-on-surface-disabled">—</span>);
      }
      const remaining = Math.max(effectiveQtyFor(line, edit) - netReceivedFor(line), 0);
      return struck(
        !!edit?.removed,
        <span className={remaining > 0 ? 'text-on-surface font-medium' : 'text-on-surface-disabled'}>
          {remaining}
        </span>
      );
    },
  },
  {
    id: 'unit_cost',
    header: 'Unit Cost',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row, table }) => {
      const edit = editHandleFor(table, row.original);
      if (!edit || !edit.canEdit('unit_cost_cents')) return formatCents(row.original.unit_cost_cents);
      return struck(
        edit.removed,
        <TextField
          size="sm"
          type="text"
          inputMode="decimal"
          className="!w-20 text-right"
          value={edit.draft.unit_cost}
          disabled={edit.removed}
          placeholder="0.00"
          aria-label={`${lineLabel(row.original)} unit cost`}
          onChange={(e) => edit.onChange({ unit_cost: e.target.value })}
        />
      );
    },
  },
  {
    id: 'line_total',
    header: 'Line Total',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    // No input: a line total is quantity times cost, and the two cells that
    // hold those are one and four columns left. It follows the draft.
    cell: ({ row, table }) => {
      const edit = editHandleFor(table, row.original);
      return struck(
        !!edit?.removed,
        formatCents(edit ? draftLineTotalCents(edit.draft) : row.original.line_total_cents)
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    meta: { align: 'center' },
    cell: ({ row, table }) => struck(isRemoved(table, row.original), statusPill(row.original)),
  },
  {
    id: 'arrival',
    header: 'Expected Arrival',
    meta: { numeric: true, cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row, table }) => {
      const edit = editHandleFor(table, row.original);
      if (!edit || !edit.canEdit('expected_arrival_date')) {
        return formatEventDate(row.original.expected_arrival_date, 'MMM D, YYYY') || '—';
      }
      return struck(
        edit.removed,
        <TextField
          size="sm"
          type="date"
          className="!w-36"
          value={edit.draft.expected_arrival_date}
          disabled={edit.removed}
          aria-label={`${lineLabel(row.original)} expected arrival`}
          onChange={(e) => edit.onChange({ expected_arrival_date: e.target.value })}
        />
      );
    },
  },
  // -------------------------------------------------------------------------
  // The invoice side of the three-way match, behind a divider (the border-l on
  // both columns) so where the ORDER's own data ends and what vendors have
  // BILLED against it begins is visible, per Mannie's redline (2026-08-24).
  // Financial-only (columnVisibility on the page) and read mode only: edit mode
  // is the buyer correcting the document, and these are not the document.
  // -------------------------------------------------------------------------
  {
    id: 'billed',
    header: 'Billed',
    meta: {
      align: 'right',
      numeric: true,
      // All three bands, or the divider stops one row short at the totals.
      headerClass: 'border-l-2 border-[rgb(70_70_70/0.60)]',
      cellClass: 'border-l-2 border-[rgb(70_70_70/0.60)]',
      footerClass: 'border-l-2 border-[rgb(70_70_70/0.60)]',
    },
    cell: ({ row, table }) => {
      const b = billingFor(table, row.original);
      if (!b || (b.billed_amount_cents === 0 && b.invoices.length === 0)) {
        return <span className="text-on-surface-disabled">—</span>;
      }
      // Billed money outran the goods (net of returns), and WHY decides the
      // tone. A return in progress is a gap the vendor closes when they answer,
      // so it stays neutral; billing for goods that never came is the amber one
      // somebody has to chase. Recorded and flagged either way, never blocked.
      const returnPending = b.ahead_reason === 'return_pending';
      return (
        <span
          className={
            b.ahead_of_receiving && !returnPending ? 'text-amber-300 font-medium' : 'text-on-surface'
          }
        >
          {formatCents(b.billed_amount_cents)}
          <Appear
            show={!!b.ahead_of_receiving}
            className={`ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ring-1 ring-inset ${
              returnPending
                ? 'bg-surface-container-high text-on-surface-variant ring-outline-variant'
                : 'bg-amber-500/15 text-amber-300 ring-amber-500/30'
            }`}
          >
            {returnPending ? 'return in progress' : 'billed beyond received'}
          </Appear>
        </span>
      );
    },
  },
  {
    id: 'invoices',
    header: 'Invoices',
    meta: { cellClass: 'whitespace-nowrap' },
    // The invoices billing this line, as links into the queue. A dimmed italic
    // chip is one still in review (or voided): its figure is a claim, not yet
    // in the Billed sum one cell left.
    cell: ({ row, table }) => {
      const b = billingFor(table, row.original);
      if (!b || b.invoices.length === 0) return <span className="text-on-surface-disabled">—</span>;
      return (
        <span className="inline-flex gap-1.5 flex-wrap">
          {b.invoices.map((inv) => (
            <Link
              key={inv.vendor_invoice_id}
              to={`/invoices/${inv.vendor_invoice_id}`}
              className={`font-mono text-[11px] hover:underline ${
                inv.counted ? 'text-primary' : 'text-on-surface-variant italic'
              }`}
            >
              {inv.invoice_number || 'unnumbered'}
            </Link>
          ))}
        </span>
      );
    },
  },
  {
    // DUPLICATE — the only way a new line reaches an existing order.
    //
    // WHY IT EXISTS (Mannie's boss): you could not order the same SKU twice on
    // one PO, and a buyer splitting a part across two delivery dates needs
    // exactly that. Nothing in the database or the API ever refused it; the page
    // simply had no way to add a line, so the advice was "put it on a new order",
    // which is a second PO number for one purchase.
    //
    // The copy is STAGED, not written: it appears as an editable row and reaches
    // the database only when Save is pressed, the same shape Odoo and ERPNext use
    // for their order-line grids. So a mis-click costs a Discard, not a patch.
    //
    // Edit mode only, like Remove, and for the same reason: appending a line is
    // a hard edit, and the page is handed `meta` only where the server allows one.
    id: 'duplicate',
    header: 'Duplicate',
    meta: { align: 'center' },
    cell: ({ row, table }) => {
      const line = row.original;
      const edit = editHandleFor(table, line);
      if (!edit) return null;
      // A row on its way out is not a row to copy. Its own Keep button is the
      // way back, one column right.
      if (edit.removed) {
        // On a <span>, never the control: a title on a button replaces its
        // accessible name. Same reason the Remove column hangs its own there.
        return (
          <Tooltip label="This line is marked for removal. Press Keep to put it back before copying it.">
            <span className="text-on-surface-disabled text-xs">—</span>
          </Tooltip>
        );
      }
      return (
        // NO `title` here. A title on a <button> REPLACES its accessible name
        // rather than supplementing it, so the tooltip becomes what a screen
        // reader announces and "Duplicate" is never spoken. What the control does
        // is said once, in the edit strip above the table, where it is visible to
        // everyone. The existing Remove column gets away with a title because it
        // hangs on a <span>, not on its button.
        <button
          type="button"
          onClick={edit.onDuplicate}
          className={buttonClass('quiet', 'sm')}
        >
          Duplicate
        </button>
      );
    },
  },
  {
    // Edit mode's own column, hidden the rest of the time (`columnVisibility`
    // on the page). It carries NO strike-through: on a row marked for removal
    // Keep is the way back, and a dimmed struck-out control does not read as
    // one. The row's red wash comes from rowProps instead.
    //
    // No handle means an EMPTY cell, which is what a soft-only document gets.
    // The old edit table put this header over nothing there — 12 headers, 11
    // cells, the row one short at its right edge — because the header was
    // conditional on `editing` and the cell on the handle. One column def
    // cannot hold those two conditions apart.
    id: 'remove',
    header: 'Remove',
    meta: { align: 'center' },
    cell: ({ row, table }) => {
      const line = row.original;
      const edit = editHandleFor(table, line);
      if (!edit) return null;
      if (line.quantity_received > 0) {
        return (
          <Tooltip label="Goods have already been received against this line, so it cannot be removed. Lower the quantity instead.">
            <span className="text-on-surface-disabled text-xs">—</span>
          </Tooltip>
        );
      }
      // A staged copy is thrown away outright rather than marked, because there
      // is no stored row for Keep to come back to. Still says Remove: it is the
      // same intent from the buyer's side, this column's own word, and the row
      // vanishing says the rest. Deliberately NOT "Discard" — that word already
      // means "abandon every edit" on this page, and one word cannot mean two
      // things on one screen.
      if (edit.isNew) {
        return (
          <button
            type="button"
            onClick={edit.onToggleRemove}
            className={buttonClass('danger-quiet', 'sm')}
          >
            Remove
          </button>
        );
      }
      return (
        <button
          type="button"
          onClick={edit.onToggleRemove}
          className={buttonClass(edit.removed ? 'quiet' : 'danger-quiet', 'sm')}
        >
          {edit.removed ? 'Keep' : 'Remove'}
        </button>
      );
    },
  },
];

// ---------------------------------------------------------------------------
// The roll-up bands. Goods are what receiving chases; services and charges are
// noise until they are the question, so each of those folds behind one row and
// says what is inside it.
// ---------------------------------------------------------------------------

/** Band order: goods first, then the extras, in the order a document reads.
 * A type this PO has no lines of drops out. */
const LINE_BAND_ORDER = ['stock', 'service', 'charge'];

/** Section names, plural, NOT PO_LINE_TYPE_LABELS: those name one line's type
 * and are what the Type pill says. A band names a section of the document. */
const LINE_BAND_NAMES: Record<string, string> = { service: 'Services', charge: 'Charges' };

/** What one band says while it is closed: what it is, how many lines, how much
 * money. Pure over the group's rows, so it holds no page state.
 *
 * It counts EVERY line in the group, cancelled ones included, because the
 * figure has to equal the sum of the Line Total column the expansion reveals.
 * That is the opposite rule from the totals band below the table, which counts
 * only what is still owed — different question, different arithmetic. */
const bandRollup = (key: string, lines: PoLine[]): string => {
  const cents = lines.reduce((sum, l) => sum + l.line_total_cents, 0);
  const count = `${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`;
  return `${LINE_BAND_NAMES[key] ?? key} · ${count} · ${formatCents(cents)}`;
};

/** The same columns wearing the totals band. TWO arrays rather than one built
 * per render: a services-only PO has no arithmetic to show, and a band reading
 * "Stock totals 0 0 0" there would state zeros as a fact.
 *
 * Swapping arrays remounts every cell, so it must never happen mid-typing. It
 * happens on exactly two events: the PO's stock composition flipping under a
 * fetch or a save, and entering or leaving edit mode (the page drops the band
 * while editing, since a total over changing drafts would lie). The caret
 * contract covers typing WITHIN a mode, which is the only place it matters. */
const PO_LINE_COLUMNS_TOTALED: ColumnDef<PoLine, any>[] = PO_LINE_COLUMNS.map((c) =>
  FOOTERS[c.id!] ? { ...c, footer: FOOTERS[c.id!] } : c
);

const ReasonModal: React.FC<{
  kind: ReasonKind;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}> = ({ kind, submitting, onClose, onConfirm }) => {
  const copy = REASON_COPY[kind];
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (copy.required && !reason.trim()) {
      setError('A reason is required, so the next person knows why.');
      return;
    }
    onConfirm(reason.trim());
  };

  return (
    <Dialog
      open
      onClose={onClose}
      busy={submitting}
      dirty={reason.trim() !== ''}
      size="md"
      title={copy.title}
      onSubmit={() => handleSubmit()}
      problem={error ? <span id="reason-modal-error">{error}</span> : undefined}
      footer={(
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={buttonClass('quiet')}
          >
            Never mind
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={buttonClass(copy.danger ? 'danger' : 'filled')}
          >
            {submitting ? 'Working…' : copy.confirmLabel}
          </button>
        </>
      )}
    >
      <label className="block">
        <span className={labelClass}>{copy.fieldLabel}</span>
        <TextArea
          rows={3}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError(null);
          }}
          aria-describedby={error ? 'reason-modal-error' : undefined}
          autoFocus
        />
      </label>
    </Dialog>
  );
};

const PurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const poApi = usePoApi();
  const api = useApi();
  const toast = useToast();
  const boardApi = useMatchBoardApi();
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // The billing read (financial only): one fetch feeds the rail AND the two
  // invoice columns on the lines table, so the two can never disagree. Buyers
  // never fetch it — the route is behind the strict financial gate anyway.
  const user = useUserStore((s) => s.user);
  const canSeeBilling = !!user?.can_financial;
  const apApi = useApInvoicesApi();
  const [billing, setBilling] = useState<OrderBilling | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(false);
  // Out-of-order guard: refetches fire from upload and save, and the slower of
  // two responses must not overwrite the newer one. Monotonic, compared at
  // resolve time.
  const billingSeq = useRef(0);
  const fetchBilling = useCallback(async () => {
    if (!canSeeBilling) return;
    const seq = ++billingSeq.current;
    setBillingLoading(true);
    try {
      const fresh = await apApi.listForOrder(id);
      if (seq !== billingSeq.current) return;
      setBilling(fresh);
      setBillingError(false);
    } catch {
      if (seq !== billingSeq.current) return;
      // KEEP whatever the rail already holds: a failed refetch blanking a
      // loaded rail would state "no invoices" as a fact. With nothing held yet
      // the panel offers the retry itself.
      setBillingError(true);
    } finally {
      if (seq === billingSeq.current) setBillingLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canSeeBilling]);
  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);
  // A payment save hands back the server's own row; folding it in locally is
  // both sufficient (a note changes nothing else on this read) and safer than
  // the refetch it replaces, which could race a second save or fail and blank
  // the rail. The invoice-level derived flags (counted, ahead, spans) do not
  // move on a note change, so only the note fields are folded.
  const applyInvoicePatch = useCallback((updated: VendorInvoice) => {
    setBilling((prev) =>
      prev
        ? {
            ...prev,
            invoices: prev.invoices.map((inv) =>
              inv.id === updated.id
                ? {
                    ...inv,
                    payment_status: updated.payment_status,
                    payment_status_at: updated.payment_status_at,
                    payment_on_hold: updated.payment_on_hold,
                    payment_hold_reason: updated.payment_hold_reason,
                    due_date: updated.due_date,
                  }
                : inv
            ),
          }
        : prev
    );
  }, []);

  // The buyer door: "here is the invoice for THIS order." The order id rides
  // as a matcher hint; printed numbers on the paper still outrank it, and a
  // mismatch comes back as an attention flag the toast passes along.
  const onInvoiceUpload = async (file: File) => {
    if (!po) return;
    setUploadingInvoice(true);
    try {
      const res = await boardApi.uploadForOrder(po.id, file);
      const paired = res.lines.filter((l) => l.purchase_order_line_id).length;
      const reasons = res.invoice.attention_reasons || [];
      const mismatch = reasons.find((r) => r.code === 'hint_order_mismatch');
      if (mismatch) {
        toast.error(mismatch.message);
      } else if (reasons.length) {
        // The person standing at the door is the best placed to catch a flag
        // (a duplicate file, a cross-vendor smell): success-with-flags is a
        // warning, never a plain success.
        toast.warning(`Read ${res.lines.length} line${res.lines.length === 1 ? '' : 's'}, flagged for review: ${reasons[0].message}`);
      } else {
        toast.success(`Read ${res.lines.length} line${res.lines.length === 1 ? '' : 's'} from ${res.invoice.vendor_observed || 'the document'}; ${paired ? `${paired} paired to this order` : 'none paired yet'}. Accounting confirms it from their queue; Bill Matching has the rest.`);
      }
      // The rail shows the new arrival in its review pile right away.
      fetchBilling();
    } catch (e: any) {
      const reason = e?.data?.error?.document_reason;
      toast.error(reason ? `Not stored: that reads as ${reason}.` : (e?.data?.error?.message || e?.message || 'Upload failed.'));
    } finally {
      setUploadingInvoice(false);
    }
  };
  const confirm = useConfirm();
  const history = useHistory();

  const [po, setPo] = useState<PoDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Edit mode. Absent drafts mean "not editing" — one flag plus drafts seeded on
  // entry, so leaving discards by construction instead of by remembering to.
  const [editing, setEditing] = useState(false);
  const [headerDraft, setHeaderDraft] = useState<HeaderDraft | null>(null);
  // "Update as you go" (migration 078): on Save, also write this order's
  // payment clock to the vendor as its default. Seeded on entering edit mode:
  // ON when the vendor has no clock recorded (filling a blank is the fast
  // path), OFF when it has one (overwriting a recorded default is a click).
  const [saveClockToVendor, setSaveClockToVendor] = useState(false);
  // Whether the user touched that box this session. A seeded tick alone never
  // writes the vendor: only a clock edited in this save, or a box the user
  // ticked, does. Opening the editor and pressing Save must not write a shared
  // record as a side effect.
  const [clockBoxTouched, setClockBoxTouched] = useState(false);
  const [lineDrafts, setLineDrafts] = useState<LineDraftMap>({});
  const [removedLineIds, setRemovedLineIds] = useState<string[]>([]);
  // Lines staged by Duplicate, in the order they were added. They have no
  // database row until Save, so they live here rather than in `po`.
  const [addedLines, setAddedLines] = useState<PoLine[]>([]);
  // The order the lines table shows while editing, stored and staged ids
  // together, charges always in their block at the bottom. What buildPatch
  // turns into positions when it differs from what the save would do anyway.
  const [lineOrder, setLineOrder] = useState<string[]>([]);
  // The row under the pointer mid-drag, for its tint.
  const [carriedId, setCarriedId] = useState<string | null>(null);
  // A MONOTONIC counter, never addedLines.length: staging two copies then
  // discarding the first would otherwise hand the next copy an id already in
  // use, and two rows sharing a key is two rows sharing a draft.
  const newLineSeq = useRef(0);
  // WHERE FOCUS GOES WHEN A STAGED ROW IS DESTROYED. Removing a staged copy
  // deletes its row outright, which takes the button that was just clicked with
  // it. The browser then drops focus to <body>, so a keyboard user's next Tab
  // restarts at the top of the page. A stored line does not have this problem:
  // marking it for removal keeps the row mounted and swaps the control to Keep.
  const linesHeadingRef = useRef<HTMLHeadingElement>(null);
  // The Add line entry row, asked before Save whether it holds an unstaged line.
  const entryRef = useRef<PoLineEntryHandle>(null);

  const [reasonModal, setReasonModal] = useState<ReasonKind | null>(null);
  const [returningItems, setReturningItems] = useState(false);

  // Once anything has been received, the server refuses Amend outright, and
  // refuses Cancel while a line is only PARTIALLY received (cancelling it would
  // drop its received units out of the header percentage). A fully-received
  // line is left alone by cancel, so the two conditions are deliberately not
  // the same one.
  const hasAnyReceipts = (po?.lines || []).some((l) => Number(l.quantity_received) > 0);
  const hasPartialReceipts = (po?.lines || []).some(
    (l) => Number(l.quantity_received) > 0 && (l.status === 'pending' || l.status === 'partial')
  );
  // The read-only table's bands. Goods wear NO band at all: they are what the
  // page is for. Services and charges each roll up behind one row, collapsed
  // while there are goods to read first — and EXPANDED on an order that is
  // nothing but services or charges, since a page hiding everything it has
  // behind one row answers nothing.
  const hasStockLines = (po?.lines || []).some((l) => l.line_type === 'stock');
  const lineBands = useMemo<GroupByOpts<PoLine>>(
    () => ({
      key: (line) => line.line_type,
      order: (keys) => LINE_BAND_ORDER.filter((k) => keys.includes(k)),
      renderHeader: (key, lines) => (key === 'stock' ? null : bandRollup(key, lines)),
      collapsible: (key) => key !== 'stock',
      initialCollapsed: () => hasStockLines,
    }),
    [hasStockLines]
  );
  // The Remove and Duplicate columns belong to edit mode; the two invoice
  // columns belong to a financial read view with any invoices to show (an
  // all-dash pair on every untouched order would be noise). Memoized on what
  // they depend on: a fresh object literal per render invalidates tanstack's
  // per-row visible-cell memo every render.
  const showInvoiceCols = !editing && canSeeBilling && !!billing && billing.invoices.length > 0;
  // Duplicate and Remove change the SHAPE of the order, so they show only while
  // the server says the shape is open (`editable.lines`): an issued order's edit
  // mode offers its quantity and date inputs with no add or remove beside them.
  const structuralEdit = editing && !!po?.editable.lines;
  const lineColumnVisibility = useMemo(
    () => ({
      move: structuralEdit,
      duplicate: structuralEdit,
      remove: structuralEdit,
      billed: showInvoiceCols,
      invoices: showInvoiceCols,
    }),
    [structuralEdit, showInvoiceCols]
  );
  // The billing read keyed for the cells: line id → its three-way numbers.
  const billingByLine = useMemo(
    () => (billing ? new Map(billing.lines.map((l) => [l.purchase_order_line_id, l])) : undefined),
    [billing]
  );

  /**
   * What the lines table draws: the stored lines, plus anything Duplicate has
   * staged.
   *
   * Returns `po.lines` ITSELF when nothing is staged, rather than a fresh copy.
   * DataTable rebuilds its row model when `data` changes identity, and a new
   * array every render would do that on every keystroke — which is the caret bug
   * the columns array is already kept static to avoid.
   */
  const tableLines = useMemo(() => {
    const stored = po?.lines || [];
    if (lineOrder.length === 0) return stored;
    // Edit mode: the page's order. `lineOrder` only changes on a move or a
    // stage, never on a keystroke, so this identity is as stable as the old one.
    const byId = new Map([...stored, ...addedLines].map((l) => [l.id, l]));
    return lineOrder.map((id) => byId.get(id)).filter((l): l is PoLine => !!l);
  }, [po, addedLines, lineOrder]);

  const [previewingVendor, setPreviewingVendor] = useState(false);

  const fetchPo = useCallback(async () => {
    setLoading(true);
    try {
      const data = await poApi.get(id);
      setPo(data);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchPo();
  }, [fetchPo]);

  const handleSubmit = async () => {
    if (!po) return;
    setActionLoading('submit');
    try {
      const updated = await poApi.submit(po.id);
      setPo(updated);
      toast.success('Purchase order submitted');
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Submit failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async () => {
    if (!po) return;
    const ok = await confirm({
      title: 'Approve this purchase order',
      message: `${po.po_number}\n\nThis moves it forward to the vendor. Approve it?`,
    });
    if (!ok) return;
    setActionLoading('approve');
    try {
      const updated = await poApi.approve(po.id);
      setPo(updated);
      toast.success('Purchase order approved');
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Approval failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAmend = async () => {
    if (!po) return;
    const ok = await confirm({
      title: 'Amend this purchase order',
      tone: 'danger',
      confirmLabel: 'Amend',
      message: `${po.po_number}\n\nThis cancels ${po.po_number} and creates a new draft revision with everything on it copied over at full quantities. Adjust the draft before submitting if some of it already arrived. What already arrived stays on this original PO's history.`,
    });
    if (!ok) return;
    setActionLoading('amend');
    try {
      const amended = await poApi.amend(po.id);
      toast.success(`Created ${amended.po_number}`);
      history.push(`/procurement/po/${amended.id}`);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Amend failed');
      setActionLoading(null);
    }
  };

  // No confirm dialog, unlike Amend: nothing happens to this order, and the
  // draft it makes is one Cancel away from gone. The toast names both numbers
  // because the page you land on is the copy, and the copy looks exactly like
  // what you just left.
  const handleDuplicate = async () => {
    if (!po) return;
    setActionLoading('duplicate');
    try {
      const copy = await poApi.duplicate(po.id);
      toast.success(`Created ${copy.po_number} as a copy of ${po.po_number}. Adjust the quantities and submit.`);
      if (copy.skipped_retired?.length) {
        const skus = copy.skipped_retired.map((l) => l.sku || `line ${l.line_number}`).join(', ');
        toast.warning(`Left off ${copy.skipped_retired.length === 1 ? 'a retired item' : 'retired items'}: ${skus}`);
      }
      history.push(`/procurement/po/${copy.id}`, { startEditing: true });
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Duplicate failed');
      setActionLoading(null);
    }
  };

  const handleReasonConfirm = async (reason: string) => {
    if (!po || !reasonModal) return;
    setActionLoading(reasonModal);
    try {
      let updated: PoDetail;
      if (reasonModal === 'cancel') updated = await poApi.cancel(po.id, reason);
      else if (reasonModal === 'deny') updated = await poApi.deny(po.id, reason);
      else if (reasonModal === 'reopen') updated = await poApi.reopen(po.id, reason);
      else updated = await poApi.close(po.id, reason || undefined);
      setPo(updated);
      setReasonModal(null);
      toast.success(
        reasonModal === 'cancel'
          ? 'Purchase order cancelled'
          : reasonModal === 'deny'
          ? 'Purchase order denied'
          : reasonModal === 'reopen'
          ? 'Purchase order reopened'
          : 'Purchase order closed'
      );
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePdf = async () => {
    if (!po) return;
    setActionLoading('pdf');
    try {
      await openAuthedPdf(pdfUrl(po.id));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to open the PDF');
    } finally {
      setActionLoading(null);
    }
  };

  const startEdit = () => {
    if (!po) return;
    setHeaderDraft(headerDraftFrom(po));
    setLineDrafts(lineDraftsFrom(po));
    setRemovedLineIds([]);
    setAddedLines([]);
    setLineOrder(pinnedLineIds(po.lines));
    setSaveClockToVendor(
      !!po.vendor && po.vendor.payment_terms_days === null && po.vendor.payment_terms_basis === null
    );
    setClockBoxTouched(false);
    setEditing(true);
  };

  const discardEdit = () => {
    setEditing(false);
    setHeaderDraft(null);
    setLineDrafts({});
    setRemovedLineIds([]);
    setAddedLines([]);
    setLineOrder([]);
    setCarriedId(null);
  };

  /** A staged line takes its place in the order: a charge at the bottom, anything
   * else just above the first charge, which is where the save would put it. */
  const placeStaged = (line: PoLine) => {
    setLineOrder((prev) => insertPinned(prev, line.id, (id) => {
      const known = po?.lines.find((l) => l.id === id) || addedLines.find((l) => l.id === id) || (id === line.id ? line : undefined);
      return !!known && isChargeLine(known);
    }));
  };

  /** Carry a line to a new index, clamped to its block. Pointer and keyboard
   * both land here. */
  const moveLine = (from: number, to: number) => {
    if (!po) return;
    const byId = new Map([...po.lines, ...addedLines].map((l) => [l.id, l]));
    setLineOrder((prev) => movePinned(prev, from, to, (id) => {
      const line = byId.get(id);
      return !!line && isChargeLine(line);
    }));
  };

  const stepLine = (lineId: string, delta: -1 | 1) => {
    const from = lineOrder.indexOf(lineId);
    if (from < 0) return;
    moveLine(from, from + delta);
  };

  // Duplicate lands here with `startEditing` in the route state, because the
  // one thing a buyer duplicates an order FOR is to change the quantities, and
  // making them press Edit first is a step with no decision in it. The state is
  // cleared once consumed so a reload shows the draft at rest, the way every
  // other arrival does.
  const location = useLocation<{ startEditing?: boolean } | undefined>();
  const landingInEdit = !!location.state?.startEditing;
  useEffect(() => {
    if (!po || loading || !landingInEdit) return;
    startEdit();
    history.replace(location.pathname, {});
    // A route change leaves focus on <body>. The lines heading is where the
    // job is, and it is already focusable for the staged-row case below.
    linesHeadingRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [po, loading, landingInEdit]);

  const setHeaderField = (key: keyof HeaderDraft, value: string) => {
    setHeaderDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  /** Push the saved order's clock to its vendor as the new default. Runs AFTER
   *  the order saved and never undoes it: a vendor write that fails leaves the
   *  order edit standing and says so. The vendor summary on screen follows the
   *  write, so the page does not claim a default the server has not seen. */
  const writeClockToVendor = async (saved: PoDetail): Promise<boolean> => {
    if (!saved.vendor) return false;
    const body = { payment_terms_days: saved.terms_days, payment_terms_basis: saved.terms_basis };
    try {
      await api.patch(`/procurement/vendors/${saved.vendor.id}`, { body });
      setPo((prev) => (prev && prev.vendor ? { ...prev, vendor: { ...prev.vendor, ...body } } : prev));
      return true;
    } catch (err: any) {
      toast.error(
        `The order saved, but the vendor default did not: ${err?.data?.error?.message || err?.message || 'request failed'}`
      );
      return false;
    }
  };

  const patchLineDraft = (lineId: string, patch: Partial<LineDraft>) => {
    setLineDrafts((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...patch } }));
  };

  /**
   * Another line for the same item, staged for the next Save.
   *
   * The draft is seeded from what is ON SCREEN — the source row's current draft,
   * falling back to the stored values — so duplicating a row you have just
   * corrected copies the correction rather than the old figures. That is what
   * makes the two-dates workflow one pass: fix the quantity, duplicate, change
   * the date.
   */
  const duplicateLine = (source: PoLine) => {
    newLineSeq.current += 1;
    const id = newLineId(newLineSeq.current);
    const seed = lineDrafts[source.id] || lineDraftFrom(source);
    const copy = stagedCopyOf(source, id);
    setLineDrafts((prev) => ({ ...prev, [id]: { ...seed } }));
    setAddedLines((prev) => [...prev, copy]);
    placeStaged(copy);
  };

  /**
   * A line from the entry row, staged for the next Save. Same mechanism as
   * Duplicate, a different source: what the buyer picked and typed, with the
   * header lending the arrival date and quote number the create form would have
   * stamped. It reaches the server as an append (no id), like a copy does.
   */
  const stageEntryLine = (staged: StagedLine) => {
    if (!po) return;
    newLineSeq.current += 1;
    const id = newLineId(newLineSeq.current);
    const line = stagedFromEntry(staged, id, po);
    setLineDrafts((prev) => ({ ...prev, [id]: lineDraftFrom(line) }));
    setAddedLines((prev) => [...prev, line]);
    placeStaged(line);
  };

  /**
   * The order's one discount, staged from the entry row's Discount kind. It
   * lives on the header draft, not in `addedLines`: the server stores it on the
   * purchase order, not as a line, and buildPatch sends it as header fields.
   * Staging a second one replaces the first; there is only ever one.
   */
  const stageDiscount = (next: StagedDiscount) => {
    if (headerDraft?.discount) toast.info('Replaced the discount.');
    setHeaderDraft((prev) => (prev ? { ...prev, discount: next } : prev));
  };
  const removeDiscount = () => {
    setHeaderDraft((prev) => (prev ? { ...prev, discount: null } : prev));
  };

  const toggleLineRemoval = (lineId: string) => {
    // A staged copy has nothing to mark for removal, so Discard drops it
    // outright. This is also what keeps a synthetic id out of remove_line_ids,
    // where it would 404 the whole save.
    if (isNewLineId(lineId)) {
      // BEFORE the state update, not after: once React has dropped the row there
      // is no element left to move focus off, and the browser has already sent it
      // to <body>. Landing on the section heading also says where you now are.
      linesHeadingRef.current?.focus();
      setAddedLines((prev) => prev.filter((line) => line.id !== lineId));
      setLineOrder((prev) => prev.filter((id) => id !== lineId));
      setLineDrafts((prev) => {
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
      return;
    }
    setRemovedLineIds((prev) =>
      prev.includes(lineId) ? prev.filter((x) => x !== lineId) : [...prev, lineId]
    );
  };

  const handleSaveEdit = async () => {
    if (!po || !headerDraft) return;

    // A half-typed entry row would vanish with edit mode, and the saved order
    // would look right. Staging it unasked would invent intent; saying so once
    // costs a click.
    if (entryRef.current?.hasContent()) {
      toast.error('Add what you typed in the entry row, or clear it, before saving.');
      entryRef.current.focus();
      return;
    }

    const problem = validateDrafts(po, headerDraft, lineDrafts, removedLineIds, addedLines);
    if (problem) {
      toast.error(problem);
      return;
    }

    const patch = buildPatch(po, headerDraft, lineDrafts, removedLineIds, addedLines, lineOrder);
    // The vendor write is wanted only when there is something to write AND the
    // user meant it: the box is ticked, the order will hold a clock, the
    // vendor's default is not already that clock, and either the clock is part
    // of this save or the user ticked the box themselves. A seeded tick over an
    // untouched clock writes nothing, so opening the editor and pressing Save,
    // or editing a line quantity, never rewrites the vendor as a side effect.
    const draftDays = headerDraft.terms_days.trim() === '' ? null : Number(headerDraft.terms_days);
    const draftBasis = headerDraft.terms_basis || null;
    const clockInPatch = !!patch && ('terms_days' in patch || 'terms_basis' in patch);
    const wantsVendorWrite = saveClockToVendor && !!po.vendor
      && (clockInPatch || clockBoxTouched)
      && (draftDays !== null || draftBasis !== null)
      && (po.vendor.payment_terms_days !== draftDays || po.vendor.payment_terms_basis !== draftBasis);
    // Nothing changed: leaving quietly beats a round trip that comes back
    // "Nothing to update." as if the user had done something wrong. Ticking
    // the vendor box alone is a change, to the vendor rather than the order.
    if (!patch && !wantsVendorWrite) {
      discardEdit();
      return;
    }

    // Changing the money on an order an approver is already holding invalidates
    // their Slack message and asks them again. That is a good outcome, but it
    // is somebody else's inbox, so it is confirmed rather than sprung.
    const reasks = patch ? patchReasksApproval(patch, po.editable) : false;
    if (reasks && !(await confirm({
      title: 'Save and re-ask for approval',
      message: 'This order is waiting for approval. Saving these changes cancels the approval request '
        + 'that was already sent and asks again with the new figures.\n\nSave and re-ask?',
      confirmLabel: 'Save and re-ask',
    }))) {
      return;
    }

    setActionLoading('save');
    try {
      const updated = patch ? await poApi.update(po.id, patch) : po;
      setPo(updated);
      // Line edits move percent_billed's denominator; the rail follows.
      if (patch) fetchBilling();
      const vendorUpdated = wantsVendorWrite ? await writeClockToVendor(updated) : false;
      discardEdit();
      const vendorLine = vendorUpdated ? ` ${updated.vendor?.name} now defaults to this payment clock.` : '';
      if (reasks) {
        toast.success(
          (updated.status === 'submitted'
            ? 'Saved. It is now under the approval threshold, so it released without approval.'
            : 'Saved. The approvers have been asked again with the new figures.') + vendorLine
        );
      } else if (vendorUpdated) {
        toast.success(patch ? `Changes saved. ${updated.vendor?.name} now defaults to this payment clock.`
          : `${updated.vendor?.name} now defaults to this payment clock.`);
      } else {
        toast.success('Changes saved');
      }
    } catch (err: any) {
      // Drafts are deliberately KEPT on failure — a 409 line_has_receipts means
      // one row is unremovable, not that everything typed should evaporate.
      toast.error(err?.data?.error?.message || err?.message || 'Save failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && !po) {
    return (
      <LogoSpinner label="Loading the purchase order" />
    );
  }

  if (!po) {
    return (
      <PageLayout width="wide" kind="workspace" className="pt-4">
        <p className="text-on-surface-variant text-sm">Purchase order not found.</p>
      </PageLayout>
    );
  }

  const shipTo = po.ship_to_name || po.ship_to_attn || po.ship_to_address;
  const busy = !!actionLoading;
  // WHAT THIS PAGE MAY OFFER COMES FROM THE SERVER, never from a rule written
  // here. `po.editable` is produced by the same function PurchaseOrdersService
  // .update gates on, so an input this page enables cannot be one the API
  // refuses. Adding an editable field is a server change; this file needs no
  // edit to pick it up.
  const canEditHard = po.editable.lines;
  // The short-fill tier (migration 073): the order is out, its shape is fixed,
  // and a quantity may still come DOWN with a reason, the arrival date may move.
  const canShortFill = !canEditHard && po.editable.line_fields.includes('quantity_expected');
  // Whether ANY line key is open, which is what decides if the table gets its
  // edit meta. Not `canShortFill`: that names one key for the button's wording,
  // and the server may some day open a different one on its own.
  const canEditLines = po.editable.line_fields.length > 0;
  const editHeader = editing ? headerDraft : null;
  const canEditField = (name: string) => po.editable.fields.includes(name);
  // Whether the lines table has arithmetic to show. Nothing to total means no
  // totals band at all, rather than a band of zeros on a services-only order.
  const hasStockTotals = po.lines.some(isCountedStock);
  // The meta is handed over whenever the server opens ANY line key. Each cell
  // then asks `canEdit` for its own key and renders plain text for one that is
  // closed, so an issued order's row is a quantity input beside a cost in
  // print, rather than a row of disabled boxes. A soft-only document (nothing
  // open on the lines) gets no meta and every row reads as it always did.
  //
  // Built fresh every render, which is what `meta` is FOR: the columns above
  // never move, so the input being typed in keeps its DOM node and its caret
  // while the drafts underneath it change.
  // Stored lines whose draft quantity has dropped and whose reason is still
  // blank, for the live region in the action bar.
  const linesNeedingReason = editing
    ? po.lines.filter((line) => {
      const draft = lineDrafts[line.id];
      return !!draft && isRecordedReduction(line, draft, po.editable) && !draft.reason.trim();
    })
    : [];
  const editMeta: PoLineEditMeta = {
    drafts: lineDrafts,
    removedIds: removedLineIds,
    editable: po.editable,
    positions: editing && lineOrderChanged(po, addedLines, lineOrder, removedLineIds)
      ? linePositions(lineOrder, removedLineIds)
      : undefined,
    onChangeLine: patchLineDraft,
    onToggleRemove: toggleLineRemoval,
    onDuplicate: duplicateLine,
    onStep: stepLine,
  };
  // What a screen reader calls a row while it is carried.
  const lineNameFor = (id: string) => {
    const line = tableLines.find((l) => l.id === id);
    return line ? lineLabel(line) : 'Line';
  };

  return (
    <PageLayout width="wide"
      kind="record"
      className="pt-4"
      pinnedRail
      rail={canSeeBilling ? (
        <PoBillingPanel
          billing={billing}
          loading={billingLoading}
          error={billingError}
          onRetry={fetchBilling}
          onInvoiceSaved={applyInvoicePatch}
        />
      ) : undefined}
    >
      <div className="min-w-0">
        <div className="space-y-5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-primary font-mono">{po.po_number}</h1>
            <HelpDrawer title="About this purchase order">
              <p className="text-on-surface font-medium">Reading this page</p>
              <p>
                Each line below is one thing that was ordered: a part, a service, or a
                one-off charge like freight. As boxes actually arrive, the received count on
                that line goes up. A line is not a box, it is what was promised; the boxes
                themselves are tracked over in Receiving.
              </p>
              <p className="text-on-surface font-medium">What &quot;+N over&quot; means</p>
              <p>
                Sometimes more shows up than was ordered, a vendor rounds up a reel, say.
                Firefleet records the extra rather than blocking it, and flags it with a
                badge like &quot;+5 over&quot; so it does not get missed.
              </p>
              <p className="text-on-surface font-medium">Editing an order</p>
              <p>
                While an order is a draft or waiting for approval, everything is open.
                Quantities, unit costs, dates, the ship-to block and line descriptions
                can change; a line can be added, and one with nothing received against
                it can be removed. Imported history stays that open at any status, since
                it came from the old spreadsheets rather than from here. Nothing is
                written until you press Save changes, and the subtotal at the top tracks
                the draft while you work. A stored line never turns into a different
                part: a line is a promise to a vendor, so remove it and add the right one.
              </p>
              <p>
                Once the order has been sent, Edit narrows to what the vendor can change
                by phone. A quantity can come down, with a reason, and arrival dates can
                move. The line then shows the new figure with what it first said
                underneath, so the order as issued is never lost, and the change lands
                in Activity. Nothing goes back out to the vendor and no revision is
                created. A quantity cannot go below what has already arrived; return the
                extra first. To order more, raise a new purchase order.
              </p>
              <p className="text-on-surface font-medium">Returning items</p>
              <p>
                Return items sends received goods back to the vendor. Pick the boxes and
                how many out of each, say what happened once for the whole return, and the
                stock moves straight to QC hold: still ours, not available to build with,
                and off the shelf so nobody picks it by mistake. Everything you send lands
                on a shipping manifest for that vendor, joining the one already open when
                there is one. Confirm what the vendor did, a replacement or a credit,
                on the Returns page (Warehouse, then Returns). The line still shows what
                arrived. What went back comes off what that line is still expecting, so a
                replacement lands against the same line, and a refund leaves the line short
                until you close the order.
              </p>
              <p className="text-on-surface font-medium">What Amend does</p>
              <p>
                <strong className="text-on-surface">Amend when the ORDER changes.</strong>{' '}
                It cancels this document and creates a new draft numbered -R1, because a
                revision is paper the vendor has to see and acknowledge.
              </p>
              <p>
                Once anything has been received, Amend is refused. A revision starts with
                nothing received, so those units would look like they are still coming, on
                two documents at once.
              </p>
              <p className="text-on-surface font-medium">What Duplicate order does</p>
              <p>
                <strong className="text-on-surface">Duplicate order when you want the same order AGAIN.</strong>{' '}
                This order is left as it is. You get a new draft to the same vendor with
                every line copied at last time&apos;s quantity and price, opened ready to
                edit, so changing the quantities and submitting is the whole job. Dates
                and the vendor&apos;s quote number are not copied; the ship-to and both
                note fields are, as a starting point.
              </p>
              <p className="text-on-surface font-medium">What Close does</p>
              <p>
                <strong className="text-on-surface">Close when the DELIVERY falls short.</strong>{' '}
                We sent the right order, the vendor sent less, and nothing goes back out to
                them.
              </p>
              <p>
                Nothing is rewritten. A line that ordered 100 and got 97 says 100 and 97
                forever. What stops is the chasing: the missing 3 leave the receiving
                queue, stop blocking a reorder, and Flerpie stops saying we are waiting on
                them. Closing asks why, and keeps that with who and when.
              </p>
              <p>
                Closed the wrong order, or the missing goods turned up after all? Reopen
                puts it back exactly as it was, and asks why the same way Close did. A
                cancelled order stays cancelled: its open lines were withdrawn with it.
              </p>
              <p className="text-on-surface font-medium">The approval threshold</p>
              <p>
                Any purchase order at or over the approval threshold ($2,500 unless an admin
                has changed it in Approval Settings) needs a person to approve it before it
                goes out to the vendor. Anything under goes straight through.
              </p>
              <p className="text-on-surface font-medium">Activity</p>
              <p>
                This is this order&apos;s lifecycle history plus any comments the team
                has left, in one place: created, submitted, approved, closed, amended,
                mixed in with what people actually said. Ordinary receives are not
                listed here; the boxes themselves live in Receiving, and the Lines
                table above carries the running counts. Type @ and a name to mention
                someone; they get a Slack DM. The history lines are automatic and
                cannot be edited, they are a record of what happened, not a
                conversation.
              </p>
            </HelpDrawer>
          </div>

          {po.amended_from && (
            <p className="text-xs text-on-surface-variant -mt-3">
              Amended from{' '}
              <Link to={`/procurement/po/${po.amended_from}`} className="text-primary hover:underline">
                the original PO
              </Link>
            </p>
          )}

          {/* Header card — reads as a document header: identity + status on the
              left, progress in the middle, the money figure on the right, all
              on one generously-spaced row, with the metadata grid below it. */}
          <Panel label="Order header" className="space-y-5 md:!p-6">
            <div className="flex flex-wrap items-start justify-between gap-6 pb-5 border-b border-gray-700/50">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <StatusChip status={po.status} />
                  <OriginNote origin={po.origin} />
                </div>
                <div>
                  <div className={labelClass}>Vendor</div>
                  {po.vendor ? (
                    <button
                      type="button"
                      onClick={() => setPreviewingVendor(true)}
                      className="text-lg font-medium text-on-surface hover:text-primary hover:underline"
                    >
                      {po.vendor.name}
                    </button>
                  ) : (
                    <div className="text-lg font-medium text-on-surface">—</div>
                  )}
                  <Reveal show={!!po.vendor?.on_hold}>
                    <div className="text-xs text-amber-400 mt-0.5">
                      Vendor on hold{po.vendor?.hold_reason ? `: ${po.vendor.hold_reason}` : ''}
                    </div>
                  </Reveal>
                </div>
              </div>

              <div className="flex-1 min-w-[180px] max-w-xs">
                <div className={labelClass}>Progress</div>
                <Progress value={po.percent_received} max={100} label="Received" display="percent" success className="max-w-md" />
                {/* The door between the two rooms. Receiving happens on the
                    order's receive page ("POs on the PO page, Receiving on the
                    Receiving page" is the standing ruling), so this is NAVIGATION
                    with a count, Odoo's smart-button shape: it never renders with
                    nothing behind it. */}
                {(() => {
                  const openLines = po.lines.filter(
                    (l) => l.line_type === 'stock' && (l.status === 'pending' || l.status === 'partial')
                  ).length;
                  const headerOpen = !['draft', 'closed', 'cancelled'].includes(po.status);
                  if (!openLines || !headerOpen) return null;
                  return (
                    <p className="mt-1.5 text-xs text-on-surface-variant">
                      {openLines} line{openLines === 1 ? '' : 's'} still expected.{' '}
                      <Link
                        to={`/receiving/order/${po.id}`}
                        className="text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
                      >
                        Receive this order
                      </Link>
                    </p>
                  );
                })()}
              </div>

              {/* The money. One big figure: the subtotal on an undiscounted order,
                  the TOTAL once there is a discount, with the subtotal and the
                  discount stepped above it so the arithmetic is on the page.
                  While editing these are the DRAFT's figures. A total computed
                  from quantities that are no longer on screen is worse than no
                  total. */}
              {(() => {
                const subtotal = editing
                  ? draftSubtotalCents(po, lineDrafts, removedLineIds, addedLines)
                  : po.subtotal_cents;
                const discount = editing && headerDraft ? headerDraft.discount : discountOf(po);
                const discountCents = draftDiscountCents(discount, subtotal);
                const over = discountCents > subtotal;
                return (
                  <div className="text-right">
                    {discount ? (
                      <>
                        <div className="text-xs text-on-surface-variant tabular-nums">
                          Subtotal {formatCents(subtotal)}
                          <span className="mx-1.5 text-on-surface-disabled">·</span>
                          <span className={over ? 'text-danger' : ''}>Discount -{formatCents(discountCents)}</span>
                        </div>
                        <div className={labelClass}>Total</div>
                      </>
                    ) : (
                      <div className={labelClass}>Subtotal</div>
                    )}
                    <div className="text-3xl font-bold text-on-surface tabular-nums leading-tight">
                      {formatCents(draftTotalCents(subtotal, discount))}
                    </div>
                    <Appear as="div" show={!!editing} className="text-[11px] text-primary mt-0.5">unsaved</Appear>
                  </div>
                );
              })()}
            </div>

            {editHeader ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                <EditField
                  label="Order date"
                  type="date"
                  value={editHeader.order_date}
                  locked={!canEditField('order_date')}
                  lockedHint="The date of record. Amend to change it."
                  onChange={(v) => setHeaderField('order_date', v)}
                />
                <EditField
                  label="Ship date"
                  type="date"
                  value={editHeader.ship_date}
                  onChange={(v) => setHeaderField('ship_date', v)}
                />
                <EditField
                  label="Expected date"
                  type="date"
                  value={editHeader.expected_date}
                  onChange={(v) => setHeaderField('expected_date', v)}
                />
                <EditField
                  label="Terms"
                  value={editHeader.terms}
                  placeholder={po.vendor?.payment_terms || 'Net 30'}
                  onChange={(v) => setHeaderField('terms', v)}
                />
                <EditField
                  label="Quote number"
                  value={editHeader.quote_number}
                  onChange={(v) => setHeaderField('quote_number', v)}
                />
                <EditField
                  label="Ship via"
                  value={editHeader.ship_via}
                  onChange={(v) => setHeaderField('ship_via', v)}
                />
                <PaymentClockFields
                  idPrefix="po-clock"
                  days={editHeader.terms_days}
                  basis={editHeader.terms_basis}
                  onDaysChange={(v) => setHeaderField('terms_days', v)}
                  onBasisChange={(v) => setHeaderField('terms_basis', v)}
                  labelClassName={labelClass}
                />
                {po.vendor && (
                  <Checkbox
                    className="col-span-2 self-end text-on-surface-variant"
                    checked={saveClockToVendor}
                    onChange={(e) => { setSaveClockToVendor(e.target.checked); setClockBoxTouched(true); }}
                    label={`Save as ${po.vendor.name}'s default payment clock`}
                    description={describeClock(po.vendor.payment_terms_days, po.vendor.payment_terms_basis)
                      ? `Vendor currently: ${describeClock(po.vendor.payment_terms_days, po.vendor.payment_terms_basis)}. Other orders keep theirs.`
                      : 'The vendor has no clock recorded yet. Other orders keep theirs.'}
                  />
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                <Field label="Order date" value={formatEventDate(po.order_date, 'MMM D, YYYY') || '—'} />
                <Field
                  label="Ship date"
                  value={po.ship_date ? formatEventDate(po.ship_date, 'MMM D, YYYY') : '—'}
                />
                <Field
                  label="Expected date"
                  value={po.expected_date ? formatEventDate(po.expected_date, 'MMM D, YYYY') : '—'}
                />
                <Field label="Terms" value={po.terms || '—'} />
                <Field
                  label="Payment due"
                  value={(() => {
                    const clock = po.payment_clock;
                    const words = describeClock(clock.days, clock.basis);
                    if (!words) return '—';
                    if (clock.due_date) {
                      return (
                        <>
                          <span className="tabular-nums">{formatEventDate(clock.due_date, 'MMM D, YYYY')}</span>
                          <span className="block text-xs text-on-surface-variant">{words}</span>
                        </>
                      );
                    }
                    // Counted from the order date while the real anchor is missing.
                    // "Estimated", never "no earlier than": a ship date before the
                    // order date (backfilled paper) lands the real date earlier. The
                    // reason names the field whose absence made it an estimate.
                    if (clock.estimated_due_date) {
                      return (
                        <>
                          <span className="tabular-nums">
                            Estimated {formatEventDate(clock.estimated_due_date, 'MMM D, YYYY')}
                          </span>
                          <span className="block text-xs text-on-surface-variant">{words}</span>
                          <span className="block text-xs text-amber-300/90">{estimateReason(clock.waiting_on)}</span>
                        </>
                      );
                    }
                    return (
                      <>
                        {words}
                        <span className="block text-xs text-on-surface-variant">{waitingLabel(clock.waiting_on)}</span>
                      </>
                    );
                  })()}
                />
                <Field label="Quote number" value={po.quote_number || '—'} />
                <Field label="Ship via" value={po.ship_via || '—'} />
              </div>
            )}

            {editHeader ? (
              <div>
                <div className={labelClass}>Ship to</div>
                {/* HARD, deliberately, against the buyer's "shipping info should be
                    editable": ship_via is the carrier and is soft, but this block is
                    a snapshot of where the goods were actually dispatched, which is
                    a fact about what happened rather than a field that follows later
                    corrections. Mannie's call. */}
                <div className="grid gap-3 md:grid-cols-2">
                  <TextField
                    type="text"
                    value={editHeader.ship_to_name}
                    placeholder="Name"
                    aria-label="Ship to name"
                    disabled={!canEditField('ship_to_name')}
                    onChange={(e) => setHeaderField('ship_to_name', e.target.value)}
                  />
                  <TextField
                    type="text"
                    value={editHeader.ship_to_attn}
                    placeholder="Attn"
                    aria-label="Ship to attention"
                    disabled={!canEditField('ship_to_attn')}
                    onChange={(e) => setHeaderField('ship_to_attn', e.target.value)}
                  />
                  <TextArea
                    className="md:col-span-2"
                    rows={2}
                    value={editHeader.ship_to_address}
                    placeholder="Address"
                    aria-label="Ship to address"
                    disabled={!canEditField('ship_to_address')}
                    onChange={(e) => setHeaderField('ship_to_address', e.target.value)}
                  />
                </div>
                <Reveal show={!canEditHard}>
                  <p className="mt-1 text-[11px] text-on-surface-variant">
                    Where this order was dispatched to. Amend to change it.
                  </p>
                </Reveal>
              </div>
            ) : (
              shipTo && (
                <div>
                  <div className={labelClass}>Ship to</div>
                  <div className="text-sm text-on-surface space-y-0.5">
                    {po.ship_to_name && <div>{po.ship_to_name}</div>}
                    {po.ship_to_attn && <div>Attn: {po.ship_to_attn}</div>}
                    {po.ship_to_address && (
                      <div className="whitespace-pre-line">{po.ship_to_address}</div>
                    )}
                  </div>
                </div>
              )
            )}

            {/* TWO prose fields, split by who reads them. The labels and the two
                hints are the whole guard against typing a vendor instruction into
                the internal column, so do not shorten them to fit a layout. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className={labelClass}>Internal notes</div>
                {editHeader ? (
                  <TextArea
                    rows={3}
                    value={editHeader.notes}
                    aria-label="Internal notes"
                    onChange={(e) => setHeaderField('notes', e.target.value)}
                  />
                ) : (
                  <p className="text-sm text-on-surface whitespace-pre-wrap">{po.notes || '—'}</p>
                )}
                <p className="mt-1 text-xs text-on-surface-variant">
                  Stays in the app. Approvals and imports write here too.
                </p>
              </div>

              <div>
                <div className={labelClass}>Notes to vendor</div>
                {editHeader ? (
                  <TextArea
                    rows={3}
                    value={editHeader.vendor_notes}
                    aria-label="Notes to vendor"
                    placeholder="Shipping instructions, packaging, anything the vendor should read"
                    onChange={(e) => setHeaderField('vendor_notes', e.target.value)}
                  />
                ) : (
                  <p className="text-sm text-on-surface whitespace-pre-wrap">{po.vendor_notes || '—'}</p>
                )}
                <p className="mt-1 text-xs text-on-surface-variant">Prints on the PDF, under the totals.</p>
              </div>
            </div>
          </Panel>

          {/* The decision surface. Money leaves the building on this click, so the
              ask is the page's one elevated element instead of one button among
              six. Rendered for EVERYONE by design (see the header comment): the
              backend 403s non-approvers, and hiding the ask would hide why the
              order is parked. Hidden while editing — the amber reask note below
              owns that state. */}
          <Reveal show={po.status === 'pending_approval' && !editing}>
            <section className="rounded-xl border border-primary/40 bg-primary/10 p-4 md:p-5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-bold text-on-surface">
                    This order waits on approval
                  </div>
                  <div className="text-xs text-on-surface-variant mt-0.5">
                    It meets the approval threshold, so it reaches the vendor only after an
                    approver says yes
                    {/* formatTimestamp, not formatEventDate: submitted_at is
                        timestamptz, and the UTC parse renders tomorrow's date all
                        evening (the documented date-utils trap). */}
                    {po.submitted_at ? ` · submitted ${formatTimestamp(po.submitted_at)}` : ''}
                    .
                  </div>
                </div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={busy}
                    className={buttonClass('filled')}
                  >
                    {actionLoading === 'approve' ? 'Approving…' : 'Approve order'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReasonModal('deny')}
                    disabled={busy}
                    className={buttonClass('quiet')}
                  >
                    Deny with a reason
                  </button>
                </div>
              </div>
            </section>
          </Reveal>

          {/* Why half the inputs are grayed. Without this the page just looks
              broken: a disabled field with no reason reads as a bug, and the reason
              (this order is out the door) is the useful half of the message. */}
          <Alert show={!!editing && !canEditHard} tone="info" variant="note" live={false} className="!flex">
              <span>
              {canShortFill ? (
                <>
                  This order has been sent. A quantity can come down if the vendor cannot
                  fill it, with a reason, and arrival dates can move. Pricing, descriptions,
                  the order date, ship-to, and adding or removing lines are fixed. To order
                  more, raise a new purchase order.
                </>
              ) : (
                <>
                  {po.status === 'cancelled' ? 'This order was cancelled' : 'This order is finished'}
                  , so its lines, quantities, pricing, order date and ship-to are fixed.
                  Dates, carrier, terms and notes can still be changed.
                </>
              )}
              </span>
            </Alert>

          {/* The approvers are holding a message quoting the current figures. Said
              before the edit, not after the save. */}
          <Alert show={!!editing && !!po.editable?.reasks_approval} tone="warning" variant="note" live={false} className="!flex">
            This order is waiting for approval. Changing the lines, quantities,
            pricing or vendor cancels the request already sent and asks the
            approvers again with the new figures.
          </Alert>

          {/* Actions. While editing, Save and Discard are the ONLY actions: Submit
              or Cancel mid-edit would throw away everything typed without saying so. */}
          {editing ? (
            <FadeIn key="editing">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={busy}
                className={buttonClass('filled')}
              >
                {actionLoading === 'save' ? 'Saving…' : 'Save changes'}
              </button>
              <button
                type="button"
                onClick={discardEdit}
                disabled={busy}
                className={buttonClass('tonal')}
              >
                Discard
              </button>
              {/* ALWAYS MOUNTED, EMPTY WHEN THERE IS NOTHING PENDING. A live region
                  has to be in the accessibility tree BEFORE its text changes: a
                  role="status" node inserted with its content already inside it is
                  the classic case screen readers skip, so a conditionally-rendered
                  region announces every staging except the first — which is the one
                  that matters. Staging is otherwise announced to nobody, since
                  Remove only restyles a row and Duplicate only appends one. */}
              <span className="text-xs text-red-300" role="status">
                {removedLineIds.length === 0
                  ? ''
                  : removedLineIds.length === 1
                    ? '1 line will be removed on save'
                    : `${removedLineIds.length} lines will be removed on save`}
              </span>
              <span className="text-xs text-primary" role="status">
                {addedLines.length === 0
                  ? ''
                  : addedLines.length === 1
                    ? '1 line will be added on save'
                    : `${addedLines.length} lines will be added on save`}
              </span>
              {/* The reason box appears mid-typing, under the quantity, with no
                  visible label; this region says it did. Same always-mounted
                  shape as the two counters above, for the same reason. */}
              <span className="text-xs text-amber-200" role="status">
                {linesNeedingReason.length === 0
                  ? ''
                  : linesNeedingReason.length === 1
                    ? `${lineLabel(linesNeedingReason[0])} needs a reason for the lower quantity`
                    : `${linesNeedingReason.length} lines need a reason for the lower quantity`}
              </span>
            </div>
            </FadeIn>
          ) : (
          <FadeIn key="viewing">
          <div className="flex flex-wrap items-center gap-2">
            {po.status === 'draft' && (
              <>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={busy}
                  className={buttonClass('filled')}
                >
                  {actionLoading === 'submit' ? 'Submitting…' : 'Submit'}
                </button>
                <button
                  type="button"
                  onClick={() => setReasonModal('cancel')}
                  disabled={busy}
                  className={buttonClass('danger')}
                >
                  Cancel
                </button>
              </>
            )}

            {/* Approve/Deny live in the callout above, not here: the decision is
                not one lifecycle action among six. */}

            {(po.status === 'submitted' || po.status === 'partially_received' || po.status === 'received') && (
              <>
                {/* Close leads: the common case at this stage is a short shipment
                    nobody is going to keep chasing, not a cancellation. */}
                <button
                  type="button"
                  onClick={() => setReasonModal('close')}
                  disabled={busy}
                  className={buttonClass('filled')}
                >
                  Close
                </button>
                {/* Cancel and Amend are HIDDEN once goods have arrived, rather than
                    shown and then refused. Both 409 in that state by design (see
                    the help drawer), and on a partially-received order they were
                    guaranteed errors: `partially_received` means some line has
                    received quantity, which is exactly the condition the server
                    rejects. Close is the right ending here and it stays. */}
                {!hasPartialReceipts && (
                  <button
                    type="button"
                    onClick={() => setReasonModal('cancel')}
                    disabled={busy}
                    className={buttonClass('danger')}
                  >
                    Cancel
                  </button>
                )}
                {!hasAnyReceipts && (
                  <button
                    type="button"
                    onClick={handleAmend}
                    disabled={busy}
                    className={buttonClass('tonal')}
                  >
                    {actionLoading === 'amend' ? 'Amending…' : 'Amend'}
                  </button>
                )}
                {hasAnyReceipts && (
                  <span className="text-xs text-on-surface-variant self-center">
                    Goods have arrived, so this order can only be closed.
                  </span>
                )}
              </>
            )}

            {/* Close DOES render on a fully-received order, since chunk 2: the
                gate is ERPNext's OR (`per_received < 100 OR per_billed < 100`), so
                "received in full, the bill never came, stop expecting it" is a
                legitimate close. The server still refuses `nothing_outstanding`
                when BOTH halves are complete — which pre-AP data never is, so the
                refusal is effectively unreachable until billing history exists. */}

            {/* The invoice door, on every billable status — CLOSED included, since a
                late invoice after a close is legitimate (the matcher and the board
                hold the same line). Hidden on draft/pending_approval: an order the
                vendor has never seen cannot be billed, and the server refuses it. */}
            {['submitted', 'partially_received', 'received', 'closed'].includes(po.status) && (
              <>
                <FilePick
                  accept="application/pdf,image/jpeg,image/png"
                  variant="tonal"
                  inputProps={{ 'data-testid': 'po-invoice-file-input' }}
                  onFiles={(files) => onInvoiceUpload(files[0])}
                  disabled={busy || uploadingInvoice}
                >
                  {uploadingInvoice ? 'Reading…' : 'Upload invoice'}
                </FilePick>
                {/* The other door goods can leave by. It needs receipts, not a
                    status: nothing can go back to a vendor before it arrived,
                    and a CLOSED order can still have a bad box on the shelf. */}
                {hasAnyReceipts && (
                  <button
                    type="button"
                    onClick={() => setReturningItems(true)}
                    disabled={busy}
                    className={buttonClass('tonal')}
                  >
                    Return items
                  </button>
                )}
              </>
            )}

            {/* Closed is no longer a dead end: close writes only the header, so
                reopening restores the document exactly. Cancelled has no such
                button — cancel withdrew the open lines with it. Secondary style
                on purpose: the common thing to do with a closed order is nothing. */}
            {po.status === 'closed' && (
              <button
                type="button"
                onClick={() => setReasonModal('reopen')}
                disabled={busy}
                className={buttonClass('tonal')}
              >
                Reopen
              </button>
            )}

            {/* Edit sits after the lifecycle actions on purpose: on a draft the thing
                you came to do is Submit. It now appears at EVERY status, because
                the soft fields are always editable; what changes is how much it
                offers once you are in. The label says which, so pressing it is
                never a surprise. */}
            <button
              type="button"
              onClick={startEdit}
              disabled={busy}
              className={buttonClass('tonal')}
            >
              {canEditHard ? 'Edit' : canShortFill ? 'Edit quantities and dates' : 'Edit dates and notes'}
            </button>

            {/* At every status, because re-ordering is most often done from an
                order that is long closed. Cancel and Amend hide once goods have
                arrived; this touches nothing on the order, so it does not.
                "Duplicate order", not "Duplicate": the lines table has a per-line
                Duplicate in edit mode, and the word alone would not say which. */}
            <button
              type="button"
              onClick={handleDuplicate}
              disabled={busy}
              className={buttonClass('tonal')}
            >
              {actionLoading === 'duplicate' ? 'Copying…' : 'Duplicate order'}
            </button>

            <button
              type="button"
              onClick={handlePdf}
              disabled={busy}
              className={buttonClass('tonal')}
            >
              {actionLoading === 'pdf' ? 'Opening…' : 'PDF'}
            </button>
          </div>
          </FadeIn>
          )}

          {/* Activity — absorbs the old Timeline strip. System entries here are
              read from purchase_order_events server-side (see MessageThread), so
              created/submitted/approved/denied/closed/cancelled/amended all still
              show, now alongside human comments instead of in a separate card. */}
          <MessageThread entityType="purchase_order" entityId={po.id} />

          {/* Lines. ONE table, both modes: the drafts ride in `meta` so a cell can
              hold an input the user is typing in without the columns array moving.
              What changes between modes is only what the page passes.

              The totals band leaves while editing (a total over changing drafts
              would lie, which was the old tfoot's own rule), and so does the
              grouping: the roll-up bands fold services and charges away, and a row
              whose quantity you came to fix cannot be behind a closed band. Edit
              mode is therefore flat document order, exactly as it always was. */}
          <LineOrder
            enabled={structuralEdit}
            ids={lineOrder}
            labelFor={lineNameFor}
            onMove={moveLine}
            onCarry={setCarriedId}
          >
          <DataTable<PoLine>
            data={tableLines}
            columns={!editing && hasStockTotals ? PO_LINE_COLUMNS_TOTALED : PO_LINE_COLUMNS}
            getRowId={(l) => l.id}
            groupBy={editing ? undefined : lineBands}
            columnVisibility={lineColumnVisibility}
            meta={{
              edit: editing && canEditLines ? editMeta : undefined,
              billing: showInvoiceCols ? billingByLine : undefined,
            }}
            // The row tint for a line marked for removal. A prop, not a column, so
            // closing over page state here is fine — and per-cell strike-through is
            // the columns' half of the same statement.
            rowProps={(line) => ({
              className: carriedId === line.id
                ? CARRIED_ROW
                : removedLineIds.includes(line.id) ? 'bg-red-950/20' : '',
            })}
            strip={
              <>
                {/* tabIndex -1: not in the tab order, but a valid focus target for
                    toggleLineRemoval to land on when it destroys a staged row. */}
                <h2
                  ref={linesHeadingRef}
                  tabIndex={-1}
                  className="text-sm font-semibold text-on-surface focus:outline-none"
                >
                  Lines
                </h2>
                <Appear as="div" show={!!editing && canEditHard} className="text-xs text-on-surface-variant ml-auto">
                    Quantity, cost, description and arrival date are editable. Duplicate a
                    line to order the same item against a second arrival date, or add a
                    line below. Drag a line by its grip to change the order; charges stay
                    at the bottom.
                </Appear>
                <Appear as="div" show={!!editing && canShortFill} className="text-xs text-on-surface-variant ml-auto">
                  Lower a quantity the vendor cannot fill and say why. The line keeps
                  what it first said.
                </Appear>
              </>
            }
            empty={
              <EmptyState variant="area">No lines on this purchase order.</EmptyState>
            }
          />
          </LineOrder>

          {/* The discount, beneath the lines (migration 110). Drawn here and
              not as a row in the table: it has no number, no grip, no
              quantity, and nothing receives against it. Read mode states it;
              edit mode adds a Remove, and a replacement comes through the
              entry row's Discount kind below. */}
          {(() => {
            const discount = editing && headerDraft ? headerDraft.discount : discountOf(po);
            if (!discount) return null;
            const subtotal = editing
              ? draftSubtotalCents(po, lineDrafts, removedLineIds, addedLines)
              : po.subtotal_cents;
            const cents = draftDiscountCents(discount, subtotal);
            const over = cents > subtotal;
            return (
              <div
                className="mt-2 flex flex-wrap items-baseline justify-end gap-x-4 gap-y-1 px-3 py-2 text-sm"
                data-testid="po-discount"
              >
                <span className="text-on-surface-variant">
                  Discount
                  <span className="ml-1.5 text-on-surface-variant">{describeDiscount(discount, formatCents)}</span>
                </span>
                <span className={`tabular-nums ${over ? 'text-danger' : 'text-on-surface'}`}>-{formatCents(cents)}</span>
                <Appear show={over} className="text-xs text-danger">Larger than the lines.</Appear>
                <Appear show={!!editing && canEditHard}>
                  <button
                    type="button"
                    onClick={removeDiscount}
                    className={buttonClass('danger-quiet', 'sm')}
                  >
                    Remove discount
                  </button>
                </Appear>
              </div>
            );
          })()}

          {/* Add line, while the shape of the order is still open. The create
              form's own entry row, so ordering something new on a draft or a
              pending order feels exactly like the New purchase order page did.
              What it stages lands in the table above as a "New line" and is
              written on Save, the same path a duplicated line takes. */}
          {editing && canEditHard && (
            <Panel label="Add a line" className="mt-3 sm:!p-5">
              <p className="mb-3 text-xs text-on-surface-variant">
                Add a line, or the order's discount. It joins the table above and is written when you press Save changes.
              </p>
              <PoLineEntry
                ref={entryRef}
                idPrefix="po-edit"
                onStage={stageEntryLine}
                onStageDiscount={stageDiscount}
                disabled={busy}
              />
            </Panel>
          )}
        </div>

      {reasonModal && (
        <ReasonModal
          kind={reasonModal}
          submitting={busy}
          onClose={() => setReasonModal(null)}
          onConfirm={handleReasonConfirm}
        />
      )}

      {returningItems && (
        <ReturnItemsDialog
          po={po}
          onClose={() => setReturningItems(false)}
          onDone={({ manifest }) => {
            setReturningItems(false);
            // The toast carries no link of its own (see components/Toast), so
            // the navigation IS the link: the next thing a person does with a
            // return is look at the manifest it landed on.
            // "Sent" was a lie the manifest page then contradicted: the slip
            // is still a DRAFT until somebody marks it sent, and the carton has
            // not moved. Name the act that happened, then the act that is owed.
            toast.success(
              `Added to ${manifest.manifest_number}. Mark it sent when the carton ships.`
            );
            fetchPo();
            history.push(`/procurement/board-returns/${manifest.id}`);
          }}
        />
      )}

      {previewingVendor && po.vendor && (
        <VendorPreviewModal
          subject={{ mode: 'existing', vendorId: po.vendor.id, vendorName: po.vendor.name }}
          onClose={() => setPreviewingVendor(false)}
        />
      )}
      </div>
    </PageLayout>
  );
};

export default PurchaseOrderDetail;
